// Merchant self-onboarding — contract signing (PE-2097).
// Mirrors watchman's merchant-onboarding DTOs (SelfOnboard contract preview + sign),
// reached publicly (no auth) through the osprey-api iam-proxy. See selfOnboardApi.ts.

/** Matches watchman `ContractAcceptanceMethod` (@prisma/client). Const-object (not `enum`) to satisfy `erasableSyntaxOnly`. */
export const ContractAcceptanceMethod = {
  EXPLICIT_ACCEPT: 'EXPLICIT_ACCEPT',
  E_SIGNATURE: 'E_SIGNATURE',
} as const;
export type ContractAcceptanceMethod =
  (typeof ContractAcceptanceMethod)[keyof typeof ContractAcceptanceMethod];

/** watchman `ContractPreviewResponseDto` — the template assigned to the applicant. */
export interface ContractPreview {
  templateId: string;
  templateName: string;
  version: number;
  contentHtml: string;
  /** watchman `ContractType | null` — display-only here. */
  contractType: string | null;
}

/** watchman `SignContractDto` body. `signatureData` is required only for E_SIGNATURE. */
export interface SignContractPayload {
  token: string;
  acceptanceMethod: ContractAcceptanceMethod;
  signatureData?: string;
}

/** watchman `SignContractResponseDto`. */
export interface SignContractResult {
  message: string;
}

// ─── Sign-up (PE-2097) ────────────────────────────────────────────────────────
// Mirrors watchman `SelfOnboardMerchantDto` / `OnboardingTaxonomyResponseDto`.

/** watchman `OnboardingIndustryDto` / `OnboardingBusinessCategoryDto` — same shape. */
export interface OnboardingTaxonomyOption {
  id: string;
  name: string;
  slug: string;
}

/** watchman `OnboardingTaxonomyResponseDto`. Only ACTIVE entries are returned. */
export interface OnboardingTaxonomy {
  industries: OnboardingTaxonomyOption[];
  businessCategories: OnboardingTaxonomyOption[];
}

/** watchman `SelfOnboardBankDetailsDto`. `businessName` here is the ACCOUNT name, which need not match the trading name. */
export interface SelfOnboardBankDetails {
  businessName: string;
  /** NIP bank code, e.g. "058". Free text — see the note in selfOnboardApi. */
  bankCode: string;
  accountNumber: string;
}

/**
 * watchman `SelfOnboardMerchantDto`.
 *
 * ⚠️ `industryIds` and `businessCategoryIds` are `@IsUUID('4', { each: true })`
 * with `@ArrayMinSize(1)` — they are taxonomy **ids**, never names or slugs.
 *
 * ⚠️ `solutionId` is OPTIONAL here and is a **slug** (`'rack-and-box'`), not a
 * UUID. That is the opposite of the customer-signup register DTO, where the same
 * field name is required AND `@IsUUID`. Same name, different contract.
 */
export interface SelfOnboardPayload {
  businessName: string;
  companyAddress: string;
  contactFullName: string;
  contactEmail: string;
  contactPhoneNumber?: string;
  industryIds: string[];
  businessCategoryIds: string[];
  solutionId?: string;
  bankDetails: SelfOnboardBankDetails;
}

/** watchman `SelfOnboardResponseDto` (201). No session is created — approval is manual. */
export interface SelfOnboardResult {
  merchantId: string;
  message: string;
}
